/**
 * Validation utilities for form inputs
 */

export const PHONE_VALIDATION = {
  MIN_LENGTH: 9,
  MAX_LENGTH: 15,
  PREFIX: '5',
  COUNTRY_CODE: '+966',
} as const

export interface ValidationResult {
  isValid: boolean
  error?: string
}

/**
 * Validates phone number format
 * Must be between 9-15 digits
 */
export function validatePhoneNumber(phone: string): ValidationResult {
  if (!phone) {
    return { isValid: false, error: 'Phone number is required' }
  }

  if (phone.length < PHONE_VALIDATION.MIN_LENGTH) {
    return {
      isValid: false,
      error: `Phone number must be at least ${PHONE_VALIDATION.MIN_LENGTH} digits`,
    }
  }

  if (phone.length > PHONE_VALIDATION.MAX_LENGTH) {
    return {
      isValid: false,
      error: `Phone number must be at most ${PHONE_VALIDATION.MAX_LENGTH} digits`,
    }
  }

  return { isValid: true }
}

/**
 * Validates OTP code
 * Must be exactly 6 digits
 */
export function validateOTP(otp: string): ValidationResult {
  if (!otp) {
    return { isValid: false, error: 'OTP is required' }
  }

  if (otp.length !== 6) {
    return { isValid: false, error: 'OTP must be exactly 6 digits' }
  }

  if (!/^\d{6}$/.test(otp)) {
    return { isValid: false, error: 'OTP must contain only digits' }
  }

  return { isValid: true }
}

/**
 * Normalizes Arabic-Indic (٠-٩, U+0660–0669) and Persian/Extended (۰-۹, U+06F0–06F9)
 * numerals to Western digits (0-9). Non-numeral characters are left untouched.
 */
export function normalizeDigits(value: string): string {
  return value.replace(/[٠-٩۰-۹]/g, (d) => {
    const code = d.charCodeAt(0)
    return String(code >= 0x06f0 ? code - 0x06f0 : code - 0x0660)
  })
}

/**
 * Sanitizes phone number input (removes non-digits)
 */
export function sanitizePhoneInput(value: string): string {
  return value.replace(/\D/g, '').slice(0, PHONE_VALIDATION.MAX_LENGTH)
}

/**
 * Formats phone number for display
 */
export function formatPhoneNumber(phone: string): string {
  return `${PHONE_VALIDATION.COUNTRY_CODE} ${phone}`
}

/**
 * Normalizes a phone number to E.164 international format (e.g. +966501234567).
 * Backend APIs often require this format. Accepts digits with or without leading +.
 */
export function toE164PhoneNumber(phone: string): string {
  const digits = phone.replace(/\D/g, '')
  if (!digits) return ''
  return `+${digits}`
}

// ---------------------------------------------------------------------------
// Regex-based validation (driven by backend question bank validation_regex)
// ---------------------------------------------------------------------------

/**
 * Creates a RegExp from a pattern string, automatically adding the `u`
 * (unicode) flag when the pattern contains Unicode property escapes
 * like \p{L}, \p{M}, \p{Script=...}, etc.  Without the `u` flag these
 * escapes are treated as literal characters and matching silently breaks.
 */
export function createSafeRegExp(pattern: string): RegExp {
  const needsUnicode = /\\p\{/.test(pattern)
  return new RegExp(pattern, needsUnicode ? 'u' : '')
}

/**
 * Returns true when the string is a usable regex pattern (not a special token
 * like "max_date:today" that the backend also stores in validation_regex).
 */
export function isValidRegexPattern(pattern: string | null | undefined): pattern is string {
  if (!pattern) return false
  if (/^(max_date|min_date|max_value|min_value):/.test(pattern)) return false
  try {
    createSafeRegExp(pattern)
    return true
  } catch {
    return false
  }
}

/**
 * Generates a user-friendly error message from a regex pattern.
 * Tries to detect well-known patterns (Saudi phone, national ID, email, etc.)
 * and falls back to a generic message.
 */
export function getRegexValidationMessage(
  regex: string,
  isArabic: boolean,
): string {
  // Unicode name pattern — letters (\p{L}), marks (\p{M}), spaces, hyphens, apostrophes
  const nameRangeMatch = regex.match(/\{(\d+),(\d+)\}/)
  if (regex.includes('\\p{L}')) {
    if (nameRangeMatch) {
      const [, min, max] = nameRangeMatch
      return isArabic
        ? `يجب أن يحتوي على أحرف فقط (${min}–${max} حرف)`
        : `Letters, spaces, hyphens and apostrophes only (${min}–${max} characters)`
    }
    return isArabic
      ? 'يجب أن يحتوي على أحرف فقط'
      : 'Letters, spaces, hyphens and apostrophes only'
  }

  // Saudi mobile number — pattern contains both 05 and 9665
  if (regex.includes('05') && regex.includes('9665')) {
    return isArabic
      ? 'يجب أن يكون رقم جوال سعودي صحيح (مثال: 05XXXXXXXX)'
      : 'Must be a valid Saudi mobile number (e.g., 05XXXXXXXX)'
  }

  // Exact digit count: ^\d{N}$ or ^[0-9]{N}$
  const exactDigits = regex.match(/^\^(?:\\d|\[0-9\])\{(\d+)\}\$$/)
  if (exactDigits) {
    const n = exactDigits[1]
    return isArabic
      ? `يجب أن يكون ${n} أرقام بالضبط`
      : `Must be exactly ${n} digits`
  }

  // Digit range: ^\d{M,N}$
  const digitRange = regex.match(/^\^(?:\\d|\[0-9\])\{(\d+),(\d+)\}\$$/)
  if (digitRange) {
    return isArabic
      ? `يجب أن يكون بين ${digitRange[1]} و ${digitRange[2]} أرقام`
      : `Must be between ${digitRange[1]} and ${digitRange[2]} digits`
  }

  // Email pattern
  if (regex.includes('@') && (regex.includes('\\.') || regex.includes('[.]'))) {
    return isArabic
      ? 'يجب أن يكون بريد إلكتروني صحيح'
      : 'Must be a valid email address'
  }

  // Letters only
  if (/^\^\[a-z/i.test(regex) && !regex.includes('0-9') && !regex.includes('\\d')) {
    return isArabic
      ? 'يجب أن يحتوي على أحرف فقط'
      : 'Must contain only letters'
  }

  // Digits only (simple)
  if (regex === '^\\d+$' || regex === '^[0-9]+$') {
    return isArabic
      ? 'يجب أن يحتوي على أرقام فقط'
      : 'Must contain only digits'
  }

  return isArabic
    ? 'صيغة الإدخال غير صحيحة'
    : 'Invalid input format'
}

/**
 * Returns a short format hint to display below the input BEFORE the user
 * types, so they know what format is expected. Returns null when no
 * meaningful hint can be derived from the pattern.
 */
export function getRegexFormatHint(
  regex: string,
  isArabic: boolean,
): string | null {
  // Unicode name pattern
  if (regex.includes('\\p{L}')) {
    const rangeMatch = regex.match(/\{(\d+),(\d+)\}/)
    if (rangeMatch) {
      return isArabic
        ? `أحرف فقط، ${rangeMatch[1]}–${rangeMatch[2]} حرف`
        : `Letters only, ${rangeMatch[1]}–${rangeMatch[2]} characters`
    }
    return isArabic ? 'أحرف فقط' : 'Letters only'
  }

  // Saudi phone
  if (regex.includes('05') && regex.includes('9665')) {
    return isArabic
      ? 'الصيغة: 05XXXXXXXX أو +9665XXXXXXXX'
      : 'Format: 05XXXXXXXX or +9665XXXXXXXX'
  }

  // Exact digit count
  const exactDigits = regex.match(/^\^(?:\\d|\[0-9\])\{(\d+)\}\$$/)
  if (exactDigits) {
    const n = exactDigits[1]
    return isArabic ? `أدخل ${n} أرقام` : `Enter ${n} digits`
  }

  // Digit range
  const digitRange = regex.match(/^\^(?:\\d|\[0-9\])\{(\d+),(\d+)\}\$$/)
  if (digitRange) {
    return isArabic
      ? `أدخل بين ${digitRange[1]} و ${digitRange[2]} أرقام`
      : `Enter ${digitRange[1]}–${digitRange[2]} digits`
  }

  // Email
  if (regex.includes('@')) {
    return isArabic ? 'مثال: example@domain.com' : 'Example: example@domain.com'
  }

  return null
}
